import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const RESEND_COOLDOWN_SECONDS = 10 * 60;

export default function VerifyOTP() {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, setUserFromTokens, refreshUser } = useAuth();

  const email = (location.state as { email?: string })?.email || '';

  // Redirect if no email
  useEffect(() => {
    if (!email) navigate('/register');
  }, [email, navigate]);

  // 1. If already logged in (session restored / token present), skip OTP page entirely
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/choose-plan', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // 2. Handle browser bfcache back-navigation (back button restoring old page state)
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted && user) {
        navigate('/choose-plan', { replace: true });
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [user, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // take last char if paste
    setCode(newCode);
    setError('');

    // Auto-advance
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (index === 5 && value) {
      const fullCode = [...newCode.slice(0, 5), value.slice(-1)].join('');
      if (fullCode.length === 6) handleVerify(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      handleVerify(pasted);
    }
  };

  const handleVerify = async (otp?: string) => {
    const finalCode = otp || code.join('');
    if (finalCode.length < 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await AuthAPI.verifyOTP(email, finalCode);
      setUserFromTokens(data.user, data.tokens.access, data.tokens.refresh);

      // Explicitly hit /auth/me/ right after OTP verification so the
      // session/user data in state & storage is confirmed fresh from the
      // server before moving on to plan selection.
      await refreshUser();

      navigate('/choose-plan', { replace: true, state: { email } });

    } catch (err: any) {
      setError(err.message || 'Invalid OTP. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await AuthAPI.resendOTP(email);
      setResent(true);
      setCountdown(RESEND_COOLDOWN_SECONDS);
      // setCountdown(60);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setTimeout(() => setResent(false), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP.');
    } finally {
      setResending(false);
    }
  };
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="min-h-screen flex">

      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[42%] bg-hero flex-col p-12 relative overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

        <Link to="/" className="flex items-center gap-2.5 relative z-10 mb-12">
          <div
            style={{
              background: "#fff",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "4px",
              padding: "6px 10px",
              boxShadow: "0 0 10px rgba(255,255,255,0.25)",
              animation: "borderBlink 2s ease-in-out infinite",
            }}
          >
            <img
              src="/manna-plag-logo-horizontal.png?v=2"
              alt="Manna Plag"
              style={{
                height: 28,
                objectFit: "contain",
              }}
            />
          </div>
        </Link>

        <div className="relative z-10 flex-1 flex flex-col justify-center">
          <div className="w-16 h-16 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-[30px] font-black text-white leading-tight tracking-tight mb-4">
            Check your inbox
          </h2>
          <p className="text-[14px] text-white/60 leading-relaxed font-medium max-w-sm">
            We've sent a 6-digit verification code to your email address. Enter it to activate your account.
          </p>
          <div className="mt-8 p-4 bg-white/5 border border-white/10 rounded-xl">
            <p className="text-[12px] text-white/40 font-medium mb-1">Sent to</p>
            <p className="text-[14px] text-white/80 font-bold">{email}</p>
          </div>
          <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
            <p className="text-[12px] text-white/50 font-medium leading-relaxed">
              The code expires in <strong className="text-white/70">10 minutes</strong>. Check your spam folder if you don't see it.
            </p>
          </div>
        </div>

        <div className="relative z-10 mt-8">
          <p className="text-[11px] text-white/25 font-medium">
            © {new Date().getFullYear()} Manna Analytics Private Limited
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 bg-gray-50">
        <div className="w-full max-w-[400px]">

          {/* Mobile logo */}
          <Link to="/" className="flex items-center gap-2 mb-4 lg:hidden">
            {/* <div className="w-7 h-7 rounded-lg bg-navy flex items-center justify-center">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z" stroke="white" strokeWidth="1.5" fill="none" />
                <path d="M8 5L11 7V11L8 13L5 11V7L8 5Z" fill="white" />
              </svg>
            </div> */}
            {/* <span className="text-[14px] font-black text-gray-900">Manna Plag</span> */}
            <img
              src="/manna-plag-logo-horizontal.png?v=2"
              alt="Manna Plag"
              style={{
                height: 32,
                objectFit: "contain",
              }}
            />
          </Link>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8" >
            <h1 className="text-[26px] font-black text-gray-900 tracking-tight mb-1">
              Verify your email
            </h1>
            <p className="text-[13px] text-gray-500 font-medium mb-8">
              Enter the 6-digit code sent to{' '}
              <span className="text-gray-800 font-bold">{email}</span>
            </p>

            {/* 6-digit OTP input */}
            <div className="flex gap-3 mb-6 justify-center" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  className={`w-12 h-14 text-center text-[22px] font-black border-2 rounded-xl focus:outline-none transition-all ${error
                    ? 'border-red-300 bg-red-50 text-red-600'
                    : digit
                      ? 'border-navy bg-blue-50 text-navy'
                      : 'border-gray-200 bg-white text-gray-900 focus:border-navy focus:bg-blue-50'
                    }`}
                />
              ))}
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-[12px] text-red-600 font-medium">{error}</p>
              </div>
            )}

            {/* Success resend */}
            {resent && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 border border-green-100 rounded-xl">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-[12px] text-green-600 font-medium">New code sent — check your inbox.</p>
              </div>
            )}

            {/* Verify button */}
            <button
              onClick={() => handleVerify()}
              disabled={loading || code.join('').length < 6}
              className="w-full py-3 bg-navy text-white text-[14px] font-black rounded-xl hover:bg-navy-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed tracking-tight uppercase mb-4"
            >
              {loading ? 'Verifying...' : 'Verify account'}
            </button>

            {/* Resend */}
            <div className="text-center">
              <p className="text-[12px] text-gray-500 font-medium mb-2">Didn't receive the code?</p>
              {countdown > 0 ? (
                <p className="text-[12px] text-gray-400 font-medium">
                  {/* Resend in <span className="text-navy font-bold">{countdown}s</span> */}
                  Resend in <span className="text-navy font-bold">{formatTime(countdown)}</span>
                </p>
              ) : (
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="text-[13px] text-navy font-bold hover:underline disabled:opacity-50"
                >
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
              )}
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <Link to="/register" className="text-[12px] text-gray-400 font-medium hover:text-gray-600">
              ← Back to registration
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
